// Display copies of our own images. The originals live in the R2 bucket `images`, served at media.stippl.io/<key>; the
// Worker on media.stippl.io/v/* (workers/media-sizes) makes a copy at a fixed width on first view and stores it, so
// there is no per-transformation Cloudflare bill and no fallback logic here: the Worker always answers.
//
//   screens  https://media.stippl.io/v/webp/<size>/<key>   WebP q80
//   PDF      https://media.stippl.io/v/<size>/<key>        baseline JPEG q80 (@react-pdf cannot read WebP or progressive JPEG)
//
// This file is also compiled into the backend with the pdfexport components (the Dockerfile copies it by name), so it must
// stay free of app imports and must not touch `window` at module load.

const MEDIA_HOST = "media.stippl.io";
const MEDIA_ORIGIN = `https://${MEDIA_HOST}`;
const ASSETS_ORIGIN = "https://assets.stippl.io";
/** The widths the Worker serves. Anything else is a 404, so a size is always snapped to one of these. */
const SIZES = [320, 640, 1280, 2048] as const;
/** What server rendering (the PDF export, SSR) assumes: there is no screen to ask. */
const SSR_DPR = 2;

// The path in front of the key on a media.stippl.io URL: Cloudflare's transform form (cdn-cgi/image/<options>/) or one of our own copies.
const PATH_BEFORE_KEY = /^\/(?:cdn-cgi\/image\/[^/]+|v(?:\/webp)?\/\d+)\//;

export type MediaFormat = "webp" | "jpeg";

const pixelRatio = (): number => (typeof window === "undefined" ? SSR_DPR : window.devicePixelRatio || 1);

/** The smallest size that covers `cssPx` on this screen, capped at the largest. */
const pickMediaSize = (cssPx: number): number => SIZES.find((size) => size >= cssPx * pixelRatio()) ?? SIZES[SIZES.length - 1];

/** The storage key of a bare key or a media.stippl.io URL, without any query or hash; null when `src` is not ours. */
const parseKey = (src: string): string | null => {
	if (/^(?:blob|data):/i.test(src)) return null;
	if (!/^https?:\/\//i.test(src)) return src.split(/[?#]/)[0];
	try {
		const url = new URL(src);
		return url.hostname === MEDIA_HOST ? decodeURIComponent(url.pathname.replace(PATH_BEFORE_KEY, "/").slice(1)) : null;
	} catch {
		return null; // not a URL, or a malformed percent-encoding
	}
};

/**
 * The display copy of an image for an element shown `cssPx` wide (the larger side, for a cover box).
 *
 * Takes a bare storage key or a media.stippl.io URL in any form (raw, `cdn-cgi/image/...`, or one of our copies) and returns the
 * copy URL; anything else (another host, `blob:`, `data:`, empty, a key with a `/` in it) comes back unchanged, so it is safe to
 * call on a value that may be either. Any `?query` or `#hash` is dropped: stored values can carry a cache-busting one
 * (profile pictures are saved as `<userId>.jpeg?<timestamp>`), and a component that needs it re-appends it to the result.
 */
export const mediaUrl = (src: string, cssPx: number, { format = "webp" }: { format?: MediaFormat } = {}): string => {
	if (!src) return src;
	const key = parseKey(src);
	// The Worker only serves single-segment keys: it answers 404 for a "/" in the decoded key (so also for a cdn-cgi URL whose source is a full external URL).
	if (!key || key.includes("/")) return src;
	return `${MEDIA_ORIGIN}/${format === "jpeg" ? "v" : "v/webp"}/${pickMediaSize(cssPx)}/${encodeURIComponent(key)}`;
};

/**
 * The URL of a trip's cover, from `trip.coverImage`. That value is a bare key written by two clients into two buckets, so the
 * key's shape says its host: the web upload is `trips/<tripId>/cover-image-<uuid>.<ext>` (before July 2026 `<orgId>/cover-image-<uuid>`)
 * in `b2b-company-assets`, served at assets.stippl.io; the app's "pick a journal photo" stores that photo's single-segment key
 * in `images`, served at media.stippl.io. A few legacy values are already a full URL and stay as they are.
 *
 * With `cssPx` a media.stippl.io cover comes back as the sized copy (`options` is mediaUrl's: a PDF wants `format: "jpeg"`); without it,
 * as the original. assets.stippl.io has no Worker, so that host is never sized.
 */
export const coverImageUrl = (cover: string, cssPx?: number, options?: { format?: MediaFormat }): string => {
	if (!cover) return cover;
	const isUrl = /^https?:\/\//i.test(cover);
	if (!isUrl && cover.includes("/")) return `${ASSETS_ORIGIN}/${cover}`;
	if (cssPx) return mediaUrl(cover, cssPx, options);
	return isUrl ? cover : `${MEDIA_ORIGIN}/${cover}`;
};
